import { useApp, useTranslatedTag, useUserSettings } from "@next-platform/client-common-core";
import appLogger from "@next-platform/client-common-core/logger/appLogger";
import { FilterActions } from "@next-platform/client-common-spicy-ui";
import type { SearchResults } from "algoliasearch-helper";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useClearRefinements, useInstantSearch, useRefinementList } from "react-instantsearch";
import { useDebouncedCallback } from "use-debounce";

import { resolveTagConflicts } from "@/components/search/utils/filterSanitizers";
import { getTagsList } from "@/utils/getTagsList";

import applyExcludableRefinement from "./applyExcludableRefinement";
import { TAGS_CATEGORY, UPDATE_SETTINGS_DEBOUNCE_TIME } from "./constants";
import skipWhileLoading from "./skipWhileLoading";
import type { FilterOperator } from "./types";

const logger = appLogger.withTag("useTagFilter");

type OnAfterTagAction = () => void;

interface IUseTagFilterParams {
  operator: FilterOperator;
  results: SearchResults | undefined;
  useStoreTagFilters: boolean;
  isAuthenticated: boolean;
  isLoading: boolean;
  onAfterTagAction?: OnAfterTagAction;
  /** Tag vocabulary to filter facets against. Defaults to the chatbot tags. */
  allowedTagsOverride?: string[];
}

export default function useTagFilter({
  operator,
  results,
  useStoreTagFilters,
  isAuthenticated,
  isLoading,
  onAfterTagAction,
  allowedTagsOverride,
}: IUseTagFilterParams) {
  const { getTranslatedTag } = useTranslatedTag();
  const { items: tagItems, refine: refineTag } = useRefinementList({
    attribute: TAGS_CATEGORY,
    operator,
    limit: 100,
  });

  const { tagOptions: chatbotTagOptions } = useApp();
  const allowedTags = allowedTagsOverride ?? chatbotTagOptions;
  const { setIndexUiState } = useInstantSearch();

  const refIsTagSelectedInitially = useRef(false);

  // Ref to track when we're actively resetting (to prevent useEffect from re-applying tags)
  const refIsResettingTagsRef = useRef(false);

  const selectedTagsSearchClient = useMemo(() => results?._state?.facetsRefinements?.tags ?? [], [results]);

  const { refine: clearTagRefinement } = useClearRefinements({
    includedAttributes: ["tags"],
  });

  const { onUserSelectsTag, selectedTags: selectedTagsStore } = useUserSettings();

  /* This is used to get the point at which tags column at tags modal will change to only one */
  const selectedTags = useMemo(() => (useStoreTagFilters ? selectedTagsStore : []), [selectedTagsStore, useStoreTagFilters]);

  const handleResetTags = useCallback(() => {
    refIsResettingTagsRef.current = true;
    clearTagRefinement();
    if (isAuthenticated && useStoreTagFilters) {
      void onUserSelectsTag([]);
    }
  }, [clearTagRefinement, isAuthenticated, onUserSelectsTag, useStoreTagFilters]);

  const hasSearchResults = !!results;

  useEffect(() => {
    if (refIsResettingTagsRef.current) {
      refIsResettingTagsRef.current = false;
      return;
    }

    if (!hasSearchResults) return;
    if (refIsTagSelectedInitially.current || !selectedTags.length) return;

    // Use setIndexUiState to SET (not toggle) tag refinements. Unlike refineTag()
    // which is a toggle, this is idempotent — safe against component remounts and
    // React StrictMode double-effect invocations that would otherwise toggle tags OFF.
    // resolveTagConflicts enforces first-in-wins so that conflicting include/exclude
    // pairs stored in Redux/DB (e.g. ["Male", "-Male"]) are resolved before they
    // reach InstantSearch.
    setIndexUiState((prevState) => {
      const existingTags = prevState.refinementList?.[TAGS_CATEGORY] ?? [];
      const resolvedTags = resolveTagConflicts([...existingTags, ...selectedTags]);
      return {
        ...prevState,
        refinementList: {
          ...prevState.refinementList,
          [TAGS_CATEGORY]: resolvedTags,
        },
      };
    });

    refIsTagSelectedInitially.current = true;
  }, [selectedTags, hasSearchResults, setIndexUiState]);

  const updateSettings = useDebouncedCallback(async () => {
    if (!isAuthenticated) {
      return;
    }

    try {
      if (!useStoreTagFilters) {
        return;
      }
      await onUserSelectsTag(selectedTagsSearchClient);
    } catch (error) {
      const { message } = error as Error;

      logger.error("Error while updating tag settings:", message);
    }
  }, UPDATE_SETTINGS_DEBOUNCE_TIME);

  const applyTagAction = useCallback(
    (action: FilterActions, value?: string) => {
      switch (action) {
        case FilterActions.INCLUDE:
        case FilterActions.EXCLUDE:
          applyExcludableRefinement({
            action,
            value: value ?? "",
            selectedRefinements: selectedTagsSearchClient,
            refine: refineTag,
          });
          break;
        case FilterActions.RESET:
          if (!selectedTagsSearchClient.length && !selectedTags.length) return;
          handleResetTags();
          onAfterTagAction?.();
          return;
        default:
          break;
      }

      void updateSettings();
      onAfterTagAction?.();
    },
    [handleResetTags, onAfterTagAction, refineTag, selectedTags, selectedTagsSearchClient, updateSettings],
  );
  const tagsActionHandler = useMemo(() => skipWhileLoading({ isLoading, actionHandler: applyTagAction }), [isLoading, applyTagAction]);

  const tagsListData = useMemo(
    () => getTagsList({ selectedTagsSearchClient, tagItems, allowedTags, translateTag: getTranslatedTag }),
    [allowedTags, selectedTagsSearchClient, tagItems, getTranslatedTag],
  );
  const tagRefinementCount = selectedTagsSearchClient.length;
  const hasTagRefinements = tagRefinementCount > 0;

  return {
    hasTagRefinements,
    tagRefinementCount,
    tagsListData,
    applyTagAction,
    tagsActionHandler,
    selectedTagsSearchClient,
    tagItems,
    refineTag,
  };
}
